/**
 * RNR's Text, carrying the design system's type scale instead of shadcn's web one.
 *
 * Six steps and no more — the design file's rule, and the reason the h1…h4/lead/large/muted set
 * that ships with the registry component was replaced rather than added to. Colour is not a variant:
 * it is a class at the call site (`text-muted-foreground`, `text-destructive`), which is how every
 * other RNR component expresses it.
 */
import { cn } from '@/lib/utils';
import { Slot } from '@rn-primitives/slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Platform, Text as RNText, type Role } from 'react-native';

const textVariants = cva(
  cn(
    'text-foreground text-body',
    Platform.select({
      web: 'select-text',
    }),
  ),
  {
    variants: {
      variant: {
        // Display type is set in the display face — geometric digits are the point of picking it.
        display: 'font-display text-display',
        title: 'font-display text-title',
        heading: 'font-display text-heading',
        body: 'text-body',
        small: 'text-small',
        caption: 'text-caption uppercase',
      },
    },
    defaultVariants: {
      variant: 'body',
    },
  },
);

type TextVariantProps = VariantProps<typeof textVariants>;

type TextVariant = NonNullable<TextVariantProps['variant']>;

const ROLE: Partial<Record<TextVariant, Role>> = {
  display: 'heading',
  title: 'heading',
  heading: 'heading',
};

const ARIA_LEVEL: Partial<Record<TextVariant, string>> = {
  display: '1',
  title: '1',
  heading: '2',
};

const TextClassContext = React.createContext<string | undefined>(undefined);

function Text({
  className,
  asChild = false,
  variant = 'body',
  ...props
}: React.ComponentProps<typeof RNText> &
  React.RefAttributes<typeof RNText> &
  TextVariantProps & {
    asChild?: boolean;
  }) {
  const textClass = React.useContext(TextClassContext);
  const Component = asChild ? Slot : RNText;
  return (
    <Component
      className={cn(textVariants({ variant }), textClass, className)}
      role={variant ? ROLE[variant] : undefined}
      aria-level={variant ? ARIA_LEVEL[variant] : undefined}
      {...props}
    />
  );
}

export { Text, TextClassContext, textVariants };
export type { TextVariant };
